import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
	Autocomplete,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Typography,
	Box,
	InputAdornment,
	IconButton,
	Pagination,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	Divider,
} from '@mui/material';
import {
	Search as SearchIcon,
	Close as CloseIcon,
	OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { useTheme } from '../../contexts/ThemeContext';
import CustomInput from '../Inputs/CustomInput';
import SelectComponent from '../Inputs/SelectComponent';

const ITEMS_PER_PAGE = 50;

const ProductTypeSelector = ({
	productType,
	setProductType,
	loadSchema,
	productTypes,
	inputValue,
	setInputValue
}) => {
	const { theme } = useTheme();

	const [isPopupOpen, setIsPopupOpen] = useState(false);
	const [popupSearchTerm, setPopupSearchTerm] = useState('');
	const [currentPage, setCurrentPage] = useState(1);
	const [sortBy, setSortBy] = useState('alphabetical');

	const [debouncedPopupSearch, setDebouncedPopupSearch] = useState('');

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedPopupSearch(popupSearchTerm);
			setCurrentPage(1);
		}, 300);
		return () => clearTimeout(timer);
	}, [popupSearchTerm]);

	const searchProducts = useCallback((term, items) => {
		if (!term) return items;

		const lowerTerm = term.toLowerCase();
		const words = lowerTerm.split(' ').filter(word => word.length > 0);

		return items.filter(product => {
			const lowerProduct = product.toLowerCase();
			return words.every(word => lowerProduct.includes(word));
		});
	}, []);

	const autocompleteOptions = useMemo(() => {
		const searched = searchProducts(inputValue, productTypes);
		return searched.slice(0, 10);
	}, [inputValue, searchProducts, productTypes]);

	const { filteredItems, totalPages, paginatedItems } = useMemo(() => {
		let filtered = searchProducts(debouncedPopupSearch, productTypes);

		const sorted = [...filtered].sort((a, b) => {
			switch (sortBy) {
				case 'alphabetical':
					return a.localeCompare(b);
				case 'reverse':
					return b.localeCompare(a);
				default:
					return a.localeCompare(b);
			}
		});

		const totalPages = Math.ceil(sorted.length / ITEMS_PER_PAGE);
		const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
		const paginatedItems = sorted.slice(startIndex, startIndex + ITEMS_PER_PAGE);

		return {
			filteredItems: sorted,
			totalPages,
			paginatedItems
		};
	}, [debouncedPopupSearch, productTypes, sortBy, currentPage, searchProducts]);

	const handleSelection = useCallback((selectedValue) => {
		setProductType(selectedValue);
		loadSchema(selectedValue);

		setIsPopupOpen(false);
		setInputValue('');
	}, [setProductType, loadSchema]);

	const handlePopupOpen = () => {
		setIsPopupOpen(true);
		setPopupSearchTerm('');
		setCurrentPage(1);
	};

	const handlePopupClose = () => {
		setIsPopupOpen(false);
		setPopupSearchTerm('');
		setCurrentPage(1);
	};

	const handlePageChange = (event, newPage) => {
		setCurrentPage(newPage);
	};

	return (
		<>
			<Autocomplete
				fullWidth
				size="small"
				options={autocompleteOptions}
				inputValue={inputValue}
				onInputChange={(event, newInputValue) => {
					setInputValue(newInputValue);
				}}
				onChange={(event, value) => {
					if (value) {
						handleSelection(value);
					}
				}}
				renderInput={(params) => (
					<CustomInput
						{...params}
						value={inputValue}
						onChange={e => {
							setInputValue(e.target.value);
						}}
						label="Product Type"
						isError={!productType || productType.length < 1}
						helperText={
							<Box>
								<Typography
									variant="body2"
									sx={{
										fontFamily: theme.fontFamily,
										color: theme.text.primary,
										mb: 1
									}}
								>
									Select one of the Product Types or browse all options
								</Typography>
								{autocompleteOptions.length > 0 && (
									<Button
										size="small"
										variant="outlined"
										startIcon={<OpenInNewIcon />}
										onClick={handlePopupOpen}
										sx={{
											textTransform: 'none',
											fontFamily: theme.fontFamily,
											fontSize: '0.75rem'
										}}
									>
										Browse all {productTypes.length.toLocaleString()} options
									</Button>
								)}
							</Box>
						}
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									{params.InputProps.endAdornment}
									<InputAdornment position="end">
										<IconButton
											size="small"
											onClick={handlePopupOpen}
											title="Browse all options"
										>
											<SearchIcon sx={{ color: theme.text.primary }} />
										</IconButton>
									</InputAdornment>
								</>
							)
						}}
						sx={{
							".MuiAutocomplete-popupIndicator": {
								color: theme.text.primary
							},
						}}
					/>
				)}
				renderOption={(props, option) => (
					<Box key={option} component="li" {...props}>
						<Box>
							<Typography variant="body2" sx={{ fontFamily: theme.fontFamily }}>
								{option}
							</Typography>
						</Box>
					</Box>
				)}
				noOptionsText={
					<Box sx={{ textAlign: 'center', py: 2 }}>
						<Typography variant="body2" color="text.secondary" sx={{ fontFamily: theme.fontFamily }}>
							No matches found
						</Typography>
						<Button
							size="small"
							variant="text"
							onClick={handlePopupOpen}
							sx={{ mt: 1, textTransform: 'none', fontFamily: theme.fontFamily }}
						>
							Browse all options
						</Button>
					</Box>
				}
			/>
			<Dialog
				open={isPopupOpen}
				onClose={handlePopupClose}
				maxWidth="md"
				fullWidth
				PaperProps={{
					sx: { height: '80vh', display: 'flex', flexDirection: 'column', background: theme.background.primary }
				}}
			>
				<DialogTitle sx={{ pb: 1 }}>
					<Box display="flex" justifyContent="space-between" alignItems="center">
						<Typography variant="h6" sx={{ fontFamily: theme.fontFamily, color: theme.text.primary }}>
							Select Product Type
						</Typography>
						<IconButton onClick={handlePopupClose} size="small">
							<CloseIcon sx={{ color: theme.text.primary }} />
						</IconButton>
					</Box>
					<Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
						<CustomInput
							fullWidth
							size="small"
							placeholder="Search product types..."
							value={popupSearchTerm}
							onChange={(e) => setPopupSearchTerm(e.target.value)}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<SearchIcon sx={{ color: theme.text.primary }} />
									</InputAdornment>
								)
							}}
						/>
						<Box sx={{ maxWidth: "50%", minWidth: { sm: "15%", md: "10%" } }}>
							<SelectComponent
								title="Sort By"
								value={sortBy}
								size="small"
								onChange={(e) => setSortBy(e.target.value)}
								options={[
									{ name: "A-Z", value: "alphabetical" },
									{ name: "Z-A", value: "reverse" }
								]}
								width={false}
							/>
						</Box>
					</Box>

					<Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontFamily: theme.fontFamily }}>
						<Typography variant="body2" sx={{ fontFamily: theme.fontFamily, color: theme.text.tertiary }}>
							{filteredItems.length.toLocaleString()} of {productTypes.length.toLocaleString()} items
							{debouncedPopupSearch && ` matching "${debouncedPopupSearch}"`}
						</Typography>
					</Box>
				</DialogTitle>

				<DialogContent sx={{ flex: 1, overflow: 'hidden', p: 0, fontFamily: theme.fontFamily }}>
					<List sx={{
						height: '100%',
						overflow: 'auto',
						'&::-webkit-scrollbar': {
							width: '10px',
							height: '10px',
						},
						'&::-webkit-scrollbar-track': {
							backgroundColor: theme.background.primary,
							borderRadius: '15px',
							border: `1px solid ${theme.border.light}`,
						},
						'&::-webkit-scrollbar-thumb': {
							backgroundColor: theme.scrollbar,
							borderRadius: '15px',
							cursor: 'grab',
						},
						'&::-webkit-scrollbar-thumb:hover': {
							backgroundColor: theme.scrollbarHover,
						},
						'&::-webkit-scrollbar-button': {
							display: 'none',
						},
					}}>
						{paginatedItems.map((item, index) => (
							<React.Fragment key={item}>
								<ListItem disablePadding>
									<ListItemButton
										onClick={() => handleSelection(item)}
										sx={{
											'&:hover': {
												backgroundColor: 'action.hover'
											}
										}}
									>
										<ListItemText
											primary={item}
											primaryTypographyProps={{
												variant: 'body2',
												sx: { fontFamily: theme.fontFamily, color: theme.text.primary }
											}}
										/>
									</ListItemButton>
								</ListItem>
								{index < paginatedItems.length - 1 && <Divider />}
							</React.Fragment>
						))}
					</List>
				</DialogContent>

				<DialogActions sx={{ justifyContent: 'center', p: 2 }}>
					{totalPages > 1 && (
						<Pagination
							count={totalPages}
							page={currentPage}
							onChange={handlePageChange}
							color="primary"
							showFirstButton
							showLastButton
							sx={{
								'& .MuiPaginationItem-root': {
									fontFamily: theme.fontFamily,
									color: theme.text.primary,
								},
								'& .MuiPaginationItem-selectIcon': {
									color: theme.text.primary,
								},
								'& .MuiInputBase-root': {
									fontFamily: theme.fontFamily,
									color: theme.text.primary,
									backgroundColor: theme.background.secondary,
								},
								'& .MuiPaginationItem-selectLabel': {
									fontFamily: theme.fontFamily,
									color: theme.text.primary,
								},
								'& .MuiPaginationItem-actions': {
									'& .MuiIconButton-root': {
										color: theme.text.primary,
										'&.Mui-disabled': {
											color: theme.accent.disabled,
										},
									}
								},
								'& .Mui-selected': {
									background: theme.color.secondary,
									color: theme.text.negative
								}
							}}
						/>
					)}
				</DialogActions>
			</Dialog>
		</>
	);
};

export default ProductTypeSelector;